/**
 * Store Selector - Dropdown to filter deals by store
 */

import { useState, useRef, useEffect, useMemo } from 'react';
import { Store, ChevronDown, Check, Search, X } from 'lucide-react';
import { useLanguage } from '../../../../contexts/LanguageContext';

interface StoreData {
  id: string;
  name: string;
  city: string;
  state: string;
  region: 'midwest' | 'northeast' | 'southeast' | 'south' | 'west';
  country: 'US' | 'CA';
}

interface StoreSelectorProps {
  stores: StoreData[];
  selectedStoreId: string | null;
  onSelect: (storeId: string | null, storeName: string | null) => void;
  country?: string;
}

const getRegionLabels = (t: (key: string, fallback: string) => string): Record<string, string> => ({
  midwest: t('storeSelector.regions.midwest', 'Midwest'),
  northeast: t('storeSelector.regions.northeast', 'Northeast'),
  southeast: t('storeSelector.regions.southeast', 'Southeast'),
  south: t('storeSelector.regions.south', 'South'),
  west: t('storeSelector.regions.west', 'West'),
});

const REGION_ORDER = ['northeast', 'midwest', 'southeast', 'south', 'west'];

export default function StoreSelector({ stores, selectedStoreId, onSelect, country }: StoreSelectorProps) {
  const { t } = useLanguage();
  const REGION_LABELS = getRegionLabels(t);
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Filter stores by country and search
  const filteredStores = useMemo(() => {
    let result = stores;

    // Filter by country if specified
    if (country) {
      result = result.filter(s => s.country === country);
    }

    // Filter by search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(s =>
        s.name.toLowerCase().includes(query) ||
        s.city.toLowerCase().includes(query) ||
        s.state.toLowerCase().includes(query)
      );
    }

    return result;
  }, [stores, country, searchQuery]);

  // Group stores by region
  const groupedStores = useMemo(() => {
    const groups: Record<string, StoreData[]> = {};

    for (const store of filteredStores) {
      if (!groups[store.region]) {
        groups[store.region] = [];
      }
      groups[store.region].push(store);
    }

    // Sort stores within each region by city
    for (const region of Object.keys(groups)) {
      groups[region].sort((a, b) => a.city.localeCompare(b.city));
    }

    return groups;
  }, [filteredStores]);

  // Get selected store name for display
  const selectedStore = stores.find(s => s.id === selectedStoreId);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close on escape key
  useEffect(() => {
    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setIsOpen(false);
        setSearchQuery('');
      }
    }

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const handleSelect = (storeId: string | null, storeName: string | null) => {
    onSelect(storeId, storeName);
    setIsOpen(false);
    setSearchQuery('');
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    handleSelect(null, null);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 bg-white border border-gray-200 rounded-xl px-4 py-2.5 text-sm font-medium text-gray-700 hover:border-gray-300 focus:border-gray-300 focus:outline-none focus:shadow-sm transition-all duration-200"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <Store className="w-4 h-4 text-gray-400" />
        <span className="max-w-[140px] truncate">
          {selectedStore ? `${selectedStore.name}, ${selectedStore.state}` : t('storeSelector.allStores', 'All Stores')}
        </span>
        {selectedStore && (
          <button
            type="button"
            onClick={handleClear}
            className="p-0.5 hover:bg-gray-100 rounded-full transition-colors"
            aria-label={t('storeSelector.clearSelection', 'Clear store selection')}
          >
            <X className="w-3.5 h-3.5 text-gray-400" />
          </button>
        )}
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div
          className="absolute left-0 mt-2 w-72 max-h-96 overflow-hidden bg-white rounded-xl shadow-xl border border-gray-200 z-50 flex flex-col"
          role="listbox"
          aria-label={t('storeSelector.selectStore', 'Select store')}
        >
          {/* Search Input */}
          <div className="p-2 border-b border-gray-100">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                ref={searchInputRef}
                type="text"
                placeholder={t('storeSelector.searchPlaceholder', 'Search stores...')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm placeholder-gray-400 focus:bg-white focus:border-gray-300 focus:outline-none transition-all"
              />
            </div>
          </div>

          {/* Store List */}
          <div className="overflow-y-auto flex-1">
            {/* All Stores Option */}
            <button
              type="button"
              onClick={() => handleSelect(null, null)}
              className={`w-full flex items-center justify-between px-4 py-3 text-sm hover:bg-gray-50 transition-colors ${
                !selectedStoreId ? 'bg-primary-50 text-primary-700' : 'text-gray-700'
              }`}
              role="option"
              aria-selected={!selectedStoreId}
            >
              <span className="font-medium">{t('storeSelector.allStores', 'All Stores')}</span>
              {!selectedStoreId && <Check className="w-4 h-4 text-primary-600" />}
            </button>

            <div className="h-px bg-gray-100" />

            {/* Grouped Stores */}
            {REGION_ORDER.map((region) => {
              const regionStores = groupedStores[region];
              if (!regionStores || regionStores.length === 0) return null;

              return (
                <div key={region}>
                  {/* Region Header */}
                  <div className="sticky top-0 px-4 py-2 bg-gray-50 border-b border-gray-100">
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      {REGION_LABELS[region]}
                    </span>
                  </div>

                  {/* Stores in Region */}
                  {regionStores.map((store) => (
                    <button
                      key={store.id}
                      type="button"
                      onClick={() => handleSelect(store.id, store.name)}
                      className={`w-full flex items-center justify-between px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors ${
                        selectedStoreId === store.id ? 'bg-primary-50 text-primary-700' : 'text-gray-700'
                      }`}
                      role="option"
                      aria-selected={selectedStoreId === store.id}
                    >
                      <span className="flex flex-col items-start">
                        <span className="font-medium">{store.name}</span>
                        <span className="text-xs text-gray-400">{store.city}, {store.state}</span>
                      </span>
                      {selectedStoreId === store.id && <Check className="w-4 h-4 text-primary-600" />}
                    </button>
                  ))}
                </div>
              );
            })}

            {/* Empty State */}
            {filteredStores.length === 0 && (
              <div className="px-4 py-8 text-center">
                <p className="text-sm text-gray-500">{t('storeSelector.noStoresFound', 'No stores found')}</p>
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery('')}
                    className="mt-2 text-sm text-primary-600 hover:text-primary-700"
                  >
                    {t('storeSelector.clearSearch', 'Clear search')}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
